import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  Modal,
} from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { ArrowLeftIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";

type TeamMember = {
  id: string;
  numericId: number;
  employeeId: string;
  isActive: boolean;
  isReferent: boolean;
  createdAt: string;
  employee: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    companyId: number;
    company: {
      id: number;
      name: string;
    } | null;
  } | null;
};

type Team = {
  id: number;
  yardId: number;
  contractId: number;
  name: string;
  description: string | null;
  delaiPourVisaDays: number | null;
  createdAt: string;
  yard: {
    id: number;
    name: string;
    moaCompanyId: number | null;
    moaCompany: { id: number; name: string } | null;
  };
  contract: {
    id: number;
    performer: { id: number; name: string };
  };
  teamMembers: TeamMember[];
};

type Employee = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  companyId: number;
  company: { id: number; name: string } | null;
};

export function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [removeMemberModalOpen, setRemoveMemberModalOpen] = useState(false);
  const [removingMember, setRemovingMember] = useState<TeamMember | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");

  const numericTeamId = Number(teamId);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiClient.GET("/api/admin/teams/{id}", {
      params: { path: { id: numericTeamId } },
    });
    if (error) {
      setLoading(false);
      return;
    }
    setTeam({
      ...data.data.team,
      teamMembers: data.data.team.teamMembers.map((m) => ({
        ...m,
        id: String(m.id),
        numericId: m.id,
      })),
    });
    setLoading(false);
  }, [numericTeamId]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const fetchEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    const { data } = await apiClient.GET("/api/admin/employees");
    if (isNotNil(data)) {
      setEmployees(
        data.data.employees.map((e) => ({
          id: e.id,
          firstName: e.firstName,
          lastName: e.lastName,
          companyId: e.companyId,
          company: { id: e.company.id, name: e.company.name },
        })),
      );
    }
    setLoadingEmployees(false);
  }, []);

  const handleOpenAddMember = () => {
    fetchEmployees();
    setSelectedEmployeeId("");
    setAddMemberModalOpen(true);
  };

  const handleAddMember = async () => {
    if (!selectedEmployeeId) return;

    setIsAdding(true);
    const { error } = await apiClient.POST("/api/admin/teams/{id}/members", {
      params: { path: { id: numericTeamId } },
      body: { employeeId: selectedEmployeeId },
    });

    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de l'ajout du membre",
      });
      setIsAdding(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: "Membre ajouté avec succès",
    });

    setAddMemberModalOpen(false);
    setSelectedEmployeeId("");
    setIsAdding(false);
    fetchTeam();
  };

  const handleRemoveMember = (member: TeamMember) => {
    setRemovingMember(member);
    setRemoveMemberModalOpen(true);
  };

  const confirmRemoveMember = async () => {
    if (isNil(removingMember)) return;

    setIsRemoving(true);
    const { error } = await apiClient.DELETE(
      "/api/admin/teams/{id}/members/{memberId}",
      {
        params: {
          path: { id: numericTeamId, memberId: removingMember.numericId },
        },
      },
    );

    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la suppression du membre",
      });
      setIsRemoving(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: "Membre supprimé avec succès",
    });

    setRemoveMemberModalOpen(false);
    setRemovingMember(null);
    setIsRemoving(false);
    fetchTeam();
  };

  const existingMemberIds = useMemo(
    () => new Set(team?.teamMembers.map((m) => m.employeeId) ?? []),
    [team],
  );

  const availableEmployees = useMemo(
    () => employees.filter((e) => !existingMemberIds.has(e.id)),
    [employees, existingMemberIds],
  );

  const memberColumns: DataTableColumn<TeamMember>[] = [
    {
      id: "name",
      header: "Nom",
      accessor: (row) =>
        [row.employee?.firstName, row.employee?.lastName]
          .filter(Boolean)
          .join(" ") || "-",
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "company",
      header: "Société",
      accessor: (row) => row.employee?.company?.name ?? "-",
      loadingCell: "text",
    },
    {
      id: "isReferent",
      header: "Référent",
      accessor: (row) => (row.isReferent ? "Oui" : "Non"),
      cell: ({ row }) =>
        row.isReferent ? <Badge variant="purple">Référent</Badge> : null,
      loadingCell: "badge",
    },
    {
      id: "isActive",
      header: "Statut",
      accessor: (row) => (row.isActive ? "Actif" : "Inactif"),
      cell: ({ row }) => (
        <Badge variant={row.isActive ? "green" : "red"}>
          {row.isActive ? "Actif" : "Inactif"}
        </Badge>
      ),
      loadingCell: "badge",
    },
    {
      id: "actions",
      header: "Actions",
      accessor: () => "",
      cell: ({ row }) => (
        <Button
          plain
          onClick={() => handleRemoveMember(row)}
          aria-label="Retirer"
        >
          <TrashIcon className="size-4" />
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex min-h-[200px] items-center justify-center text-gray-500">
          Chargement...
        </div>
      </div>
    );
  }

  if (isNil(team)) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex min-h-[200px] items-center justify-center text-gray-500">
          Équipe introuvable
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center gap-4">
          <Button
            plain
            onClick={() => navigate("/admin/teams")}
            aria-label="Retour"
          >
            <ArrowLeftIcon className="size-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {team.name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {team.yard.name} — {team.contract.performer.name}
            </p>
          </div>
        </div>

        {isNotNil(team.description) && team.description !== "" && (
          <div className="mb-6 rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900">
            <p className="text-sm text-zinc-700 dark:text-zinc-300">
              {team.description}
            </p>
          </div>
        )}

        {isNotNil(team.delaiPourVisaDays) && (
          <div className="mb-6">
            <Badge variant="blue">
              Délai pour visa : {team.delaiPourVisaDays} jour
              {team.delaiPourVisaDays !== 1 ? "s" : ""}
            </Badge>
          </div>
        )}

        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Membres ({team.teamMembers.length})
          </h3>
          <Button onClick={handleOpenAddMember}>
            <PlusIcon data-slot="icon" className="size-4" />
            Ajouter un membre
          </Button>
        </div>

        <DataTable
          columns={memberColumns}
          data={team.teamMembers}
          isLoading={false}
          isEmpty={team.teamMembers.length === 0}
          emptyState={
            <div className="flex min-h-[200px] items-center justify-center text-gray-500">
              Aucun membre dans cette équipe
            </div>
          }
        />
      </div>

      {/* Add Member Modal */}
      <Modal.Root
        open={addMemberModalOpen}
        onOpenChange={(details) => setAddMemberModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Ajouter un membre</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Sélectionnez un employé à ajouter à l'équipe.
                </Modal.Description>
                <div className="mt-4">
                  <label
                    htmlFor="employee-select"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Employé
                  </label>
                  <select
                    id="employee-select"
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    disabled={loadingEmployees}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                  >
                    <option value="">
                      {loadingEmployees
                        ? "Chargement..."
                        : availableEmployees.length === 0
                          ? "Aucun employé disponible"
                          : "Sélectionner un employé"}
                    </option>
                    {availableEmployees.map((e) => (
                      <option key={e.id} value={e.id}>
                        {[e.firstName, e.lastName].filter(Boolean).join(" ") ||
                          `Employé #${e.id}`}
                        {e.company ? ` (${e.company.name})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setAddMemberModalOpen(false)}
                  disabled={isAdding}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleAddMember}
                  disabled={isAdding || !selectedEmployeeId}
                >
                  {isAdding ? "Ajout..." : "Ajouter"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      {/* Remove Member Modal */}
      <Modal.Root
        open={removeMemberModalOpen}
        onOpenChange={(details) => setRemoveMemberModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Retirer le membre</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Êtes-vous sûr de vouloir retirer "
                  {[
                    removingMember?.employee?.firstName,
                    removingMember?.employee?.lastName,
                  ]
                    .filter(Boolean)
                    .join(" ") || "ce membre"}
                  " de l'équipe ? Cette action est irréversible.
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setRemoveMemberModalOpen(false)}
                  disabled={isRemoving}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  onClick={confirmRemoveMember}
                  disabled={isRemoving}
                >
                  {isRemoving ? "Suppression..." : "Retirer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}
